/**
 * ProfilePage — the caller-lab reads/edits its OWN Laboratoire profile (identity, contacts, fiscal ids,
 * report header). The governance trio (reference / deploy_class / tenancy_tier) is Pharmakon-issued and
 * rendered READ-ONLY (the server strips them even if injected — defense in depth). Edit is a PATCH of a
 * live row (full CRUD by correction, L-1).
 *
 * Gating: read on lab_admin.profil.read (screen ForbiddenState); the Edit affordance + the PATCH on
 * lab_admin.profil.manage via useCan (the server re-checks). A read-only viewer (read but not manage)
 * sees the profile but no edit button.
 */
import { useMemo, useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  ForbiddenState,
  FormField,
  Input,
  LoadingState,
  OfflineState,
  useToast,
} from "@/ui";
import { ApiError } from "@/api";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ADMIN_CAPS } from "../caps";
import { useLabId, useProfile, useUpdateProfile } from "../api";
import type { LabProfile, ProfilePatch } from "../types";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";

/** The editable text fields, grouped for the form layout. */
const EDITABLE_FIELDS: ReadonlyArray<{ key: keyof ProfilePatch; section: string }> = [
  { key: "raison_sociale", section: "identity" },
  { key: "nom_commercial", section: "identity" },
  { key: "nif", section: "fiscal" },
  { key: "nis", section: "fiscal" },
  { key: "rc", section: "fiscal" },
  { key: "art", section: "fiscal" },
  { key: "wilaya", section: "contact" },
  { key: "commune", section: "contact" },
  { key: "adresse", section: "contact" },
  { key: "telephone", section: "contact" },
  { key: "email", section: "contact" },
];

export function ProfilePage() {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(ADMIN_CAPS.profilRead);
  const canManage = useCan(ADMIN_CAPS.profilManage);

  const query = useProfile(labId, canRead && online);
  const update = useUpdateProfile(labId);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ProfilePatch>({});
  const [error, setError] = useState<string | null>(null);

  const profile = query.data;

  const startEdit = (p: LabProfile) => {
    setForm({
      raison_sociale: p.raison_sociale ?? "",
      nom_commercial: p.nom_commercial ?? "",
      nif: p.nif ?? "",
      nis: p.nis ?? "",
      rc: p.rc ?? "",
      art: p.art ?? "",
      wilaya: p.wilaya ?? "",
      commune: p.commune ?? "",
      adresse: p.adresse ?? "",
      telephone: p.telephone ?? "",
      email: p.email ?? "",
    });
    setError(null);
    setEditing(true);
  };

  /** Diff the form against the loaded profile → only send changed keys (PATCH; ≥1 required). */
  const dirtyPatch = useMemo<ProfilePatch>(() => {
    if (!profile) return {};
    const patch: ProfilePatch = {};
    for (const { key } of EDITABLE_FIELDS) {
      const current = (profile[key] ?? "") as string;
      const next = (form[key] ?? "") as string;
      if (next !== current) {
        // Empty string on a nullable field → null (clear it); otherwise the trimmed value.
        (patch as Record<string, string | null>)[key] = next === "" ? null : next;
      }
    }
    return patch;
  }, [profile, form]);

  const save = () => {
    setError(null);
    if (Object.keys(dirtyPatch).length === 0) {
      setError(t("admin.profile.dirty.required"));
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError(t("admin.field.invalidEmail"));
      return;
    }
    update.mutate(dirtyPatch, {
      onSuccess: () => {
        toast.success(t("admin.profile.saved"));
        setEditing(false);
      },
      onError: (err) => setError(err instanceof ApiError ? err.message : t("admin.toast.error")),
    });
  };

  const header = (
    <PageHeader
      title={t("admin.profile.title")}
      subtitle={t("admin.profile.subtitle")}
      actions={
        canManage && profile && !editing ? (
          <Button onClick={() => startEdit(profile)}>{t("admin.profile.edit")}</Button>
        ) : undefined
      }
    />
  );

  if (!canRead) return <ForbiddenState requiredCapabilities={[ADMIN_CAPS.profilRead]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={!!profile} />
      </Screen>
    );
  }
  if (query.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={5} />
      </Screen>
    );
  }
  if (query.isError) {
    return (
      <Screen>
        {header}
        <ErrorState message={query.error.message} code={query.error.code} onRetry={() => void query.refetch()} />
      </Screen>
    );
  }
  if (!profile) {
    return (
      <Screen>
        {header}
        <EmptyState title={t("admin.profile.empty.title")} description={t("admin.profile.empty.desc")} />
      </Screen>
    );
  }

  const setField = (key: keyof ProfilePatch, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const renderField = (key: keyof ProfilePatch) => {
    const label = t(`admin.profile.${key}`);
    if (editing) {
      return (
        <FormField key={key} label={label} {...(key === "email" ? { required: false } : {})}>
          {(p) => (
            <Input
              {...p}
              type={key === "email" ? "email" : "text"}
              value={(form[key] ?? "") as string}
              onChange={(e) => setField(key, e.target.value)}
            />
          )}
        </FormField>
      );
    }
    return (
      <div key={key} style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
        <span style={{ color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-label)" }}>{label}</span>
        <span>{(profile[key] as string) || "—"}</span>
      </div>
    );
  };

  const fieldsOf = (section: string) =>
    EDITABLE_FIELDS.filter((f) => f.section === section).map((f) => renderField(f.key));

  const grid = (children: React.ReactNode) => (
    <div style={{ display: "grid", gap: "var(--nv-s-4)", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
      {children}
    </div>
  );

  return (
    <Screen>
      {header}

      <Card title={t("admin.profile.section.identity")}>{grid(fieldsOf("identity"))}</Card>
      <Card title={t("admin.profile.section.fiscal")}>{grid(fieldsOf("fiscal"))}</Card>
      <Card title={t("admin.profile.section.contact")}>{grid(fieldsOf("contact"))}</Card>

      {/* Governance — always READ-ONLY (Pharmakon-issued, tied to the licence). */}
      <Card title={t("admin.profile.section.governance")}>
        <p style={{ marginTop: 0, color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-caption)" }}>
          {t("admin.governance.readonly")}
        </p>
        {grid(
          (["reference", "deploy_class", "tenancy_tier"] as const).map((key) => (
            <div key={key} style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
              <span style={{ color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-label)" }}>
                {t(`admin.profile.${key}`)}
              </span>
              <span className="nv-ltr-island" style={{ fontFamily: "var(--nv-font-mono)" }}>
                {(profile[key] as string) || "—"}
              </span>
            </div>
          )),
        )}
      </Card>

      {editing && (
        <Card>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
            {error && (
              <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
                {error}
              </p>
            )}
            <div style={{ display: "flex", gap: "var(--nv-s-2)", justifyContent: "flex-end", flexWrap: "wrap" }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                }}
              >
                {t("admin.action.cancel")}
              </Button>
              <Button onClick={save} loading={update.isPending}>
                {t("admin.action.save")}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </Screen>
  );
}
